import { useState } from "react";
import { AlertCircle, CheckCircle2, Clock } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { useGscFreshness } from "@/hooks/useClicksData";
import { GSC_STALE_DAYS, gscFreshnessTone, nextRunLine } from "@/lib/pipeline-schedule";

/**
 * "GSC Sync <date>" badge in the same language as PipelineFreshness - the last
 * day GSC actually has data. Google delivers with a ~48h lag, so recent days are
 * expected to be empty; older than GSC_STALE_DAYS it turns into a warning
 * (gscFreshnessTone; on 08.10.2026 it showed a green 2026-09-28 while the sync
 * had been dead 10 days).
 */
export const GscFreshnessBadge = () => {
  const { data: latest, isPending } = useGscFreshness();
  // Taken once per mount: GSC moves daily, and render must stay pure.
  const [now] = useState(() => Date.now());
  if (isPending) return null;

  const has = Boolean(latest);
  const tone = gscFreshnessTone(latest, now);
  const stale = tone === "warning";
  return (
    <Badge
      variant="outline"
      className={
        "rounded-full border px-2.5 py-1 font-normal " +
        (tone ? `signal-card-${tone}` : "border-border")
      }
      title={[
        has
          ? `GSC data reaches ${latest}. Google delivers with a 48h lag.` +
            (stale ? ` Older than ${GSC_STALE_DAYS} days: the GSC sync is not delivering.` : "")
          : "No GSC data available",
        nextRunLine("gsc"),
      ].join("\n")}
    >
      {stale ? (
        <AlertCircle className="size-3.5 signal-text-warning" />
      ) : has ? (
        <CheckCircle2 className="size-3.5 signal-text-positive" />
      ) : (
        <Clock className="size-3.5 text-muted-foreground" />
      )}
      <span className={tone ? `signal-text-${tone}` : "text-muted-foreground"}>
        GSC Sync{" "}
        <span className="font-semibold tabular-nums">{latest ?? "—"}</span>
      </span>
    </Badge>
  );
};
